import type { ToastEntry } from "@main/types/toast";
import { For, Show } from "solid-js";

/**
 * §3.2 / §3.11 / §UC-12: フォアグラウンド時の通知トースト (5 秒 auto-dismiss は toast-store 側)。
 *
 * デザイン整合 (U15):
 * - kp-glass 風 (rgba 暖色 + backdrop-blur) の rounded-4 カード
 * - 左に accent gradient circle + 口グリフ SVG
 * - 中央: message テキスト
 * - 右: 「👍」 / 「👎」 フィードバックボタン + ✕ 閉じる
 *
 * notificationId 経由で session-store の markNotificationBad / Good に紐付け (§UC-12)。
 * `ToastEntry` の shape は types/toast.ts に定義されている (passive な UI 部品も同じ型を使うため)。
 */

type Props = {
  toasts: () => ToastEntry[];
  onMarkBad: (notificationId: string) => void;
  onMarkGood: (notificationId: string) => void;
  onDismiss: (toastId: string) => void;
};

export function ToastStack(props: Props) {
  return (
    <div
      class="pointer-events-none fixed inset-x-0 top-5 z-50 mx-auto flex w-full max-w-md flex-col gap-2 px-4"
      role="status"
      aria-live="polite"
    >
      <For each={props.toasts()}>
        {(toast) => (
          <div
            class="pointer-events-auto flex items-center gap-3.5 rounded-4 border border-white/70 px-4 py-3.5 font-sans shadow-pop backdrop-blur-3xl backdrop-saturate-150"
            style={{
              background: "var(--color-surface-glass)",
              "box-shadow": "0 1px 0 var(--color-surface-glass-highlight) inset, var(--shadow-pop)",
            }}
          >
            <div
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white"
              style={{
                background: "linear-gradient(180deg, var(--color-accent-hot), var(--color-accent))",
              }}
            >
              {/* 口グリフ (横長楕円) */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <ellipse cx="12" cy="13" rx="6" ry="4" fill="currentColor" />
              </svg>
            </div>
            <span class="grow font-bold text-[14px] text-ink leading-[1.3]">{toast.message}</span>
            <Show when={toast.notificationId}>
              {(id) => (
                <div class="flex shrink-0 gap-1.5">
                  {/* 👍 / 👎 は対称ペア (どちらが primary という重み付けはしない)。 意味は aria-label が担保。 */}
                  <button
                    type="button"
                    class="cursor-pointer rounded-2 border border-line-2 bg-transparent px-2.5 py-1 text-[16px] leading-none hover:bg-surface"
                    onClick={() => {
                      props.onMarkGood(id());
                      props.onDismiss(toast.id);
                    }}
                    aria-label="役に立った通知としてマーク"
                  >
                    👍
                  </button>
                  <button
                    type="button"
                    class="cursor-pointer rounded-2 border border-line-2 bg-transparent px-2.5 py-1 text-[16px] leading-none hover:bg-surface"
                    onClick={() => {
                      props.onMarkBad(id());
                      props.onDismiss(toast.id);
                    }}
                    aria-label="誤検知としてマーク"
                  >
                    👎
                  </button>
                </div>
              )}
            </Show>
            <button
              type="button"
              class="cursor-pointer rounded-1 border-0 bg-transparent px-2 py-1 text-[12px] text-ink-3 hover:text-ink"
              onClick={() => props.onDismiss(toast.id)}
              aria-label="閉じる"
            >
              ✕
            </button>
          </div>
        )}
      </For>
    </div>
  );
}
